import React, { useContext } from "react";
import Button from "Components/button/Button";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import TranslatedString from "Components/TranslatedString";
import { Icon, noPostmaster, mail } from "Icons/index";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import LoadingBar from "Components/loadingBar/LoadingBar";
import { calcPercentageAndRound } from "Utils/bytesFormats";
import { MediaQueriesContext, AuthContext } from "../../App";
import { useLocation, useHistory } from "react-router-dom";

import t from "Utils/translation";
import cx from "classnames";
import s from "./listPostmasters.scss";
import { useApiQuery } from "../../hooks";

const PostmastersListScreen = () => {
  const location = useLocation();
  const history = useHistory();

  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const {
    data: postmasters,
    isLoading: arePostmastersLoading,
    pagination: postmasterPaginationInfo,
    isRefreshing: isRefreshingPostmasters,
    getData: fetchPostmasters,
  } = useApiQuery("getPostmasters", {
    enablePagination: true,
  });

  const navigateToPostmasterSettings = (code, domainCode, customerCode) => {
    const nextUrl = isLoggedUserAdmin()
      ? `/customers/${customerCode}/domains/${domainCode}/email_accounts/${code}/settings`
      : `/domains/${domainCode}/email_accounts/${code}/settings`;

    history.push({
      pathname: nextUrl,
      state: {
        prevPagination: location.search,
      },
    });
  };

  return (
    <div className="contentWrapper">
      <div className={s.listPostmasters}>
        <div className={s.headerList}>
          <div className={cx("sectionTitle", s.titleList)}>{t("postmasters.list.header.title")}</div>

          {!isMediaMobile && (
            <div className={cx("sectionDesc", s.descList)}>
              <TranslatedString>postmasters.list.header.dsc</TranslatedString>
            </div>
          )}
          <div className={s.actionsList}>
            <Button docsKey="postmasters" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
              button.docs
            </Button>
          </div>
        </div>

        <div className={s.contentList}>
          <FullPaginationControls
            className={s.filters}
            paginationInfo={postmasterPaginationInfo}
            location={location}
            refreshList={() => fetchPostmasters({ ...location.query, only_postmasters: true })}
            listFilters={["all", "enabled", "disabled"]}
            listFilterLegends={{
              all: <Icon svg={mail} width="0.75rem" height="0.75rem" />,
              enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
              disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
            }}
            isLoading={arePostmastersLoading || isRefreshingPostmasters}
            totals={postmasterPaginationInfo.totals}
          />
          <div className={s.table}>
            <ResponsiveTable
              history={history}
              areRowsLoading={arePostmastersLoading && !isRefreshingPostmasters}
              location={location}
              addIcon={noPostmaster}
              addMsg={t("table.row.add.msg.postmaster")}
              addDesc={t("table.row.add.desc.postmaster")}
              headers={[
                {
                  name: t("table.header.status"),
                  size: isMobileDevice ? "2rem" : "4rem",
                  center: true,
                },
                { name: t("table.header.domain"), hideOnMobile: true },
                { name: t("table.header.account") },
                { name: t("table.header.quote"), size: isMediaMobile ? "3rem" : "13rem" },
              ]}
              rows={postmasters.map((postmaster) => {
                const percentage = calcPercentageAndRound(postmaster.quota, postmaster.max_email_quota);

                let quotaInGB, maxQuotaInGB;
                if (postmaster.max_email_quota === 52428800) {
                  quotaInGB = Math.round((postmaster.quota / 1048576) * 100) / 100;
                  maxQuotaInGB = "50 MB";
                }

                return (
                  <tr key={postmaster.email_address} className={cx({ [s.disabled]: postmaster.status === "disabled" })}>
                    <td data-label={t("table.header.status")} className={s.cell}>
                      <DomainStatusBadge status={postmaster.status} isHealthy statusDetail={postmaster.status_detail} />
                    </td>
                    <td data-label={t("table.header.domain")} className={cx(s.cell, "hideOnMobile")}>
                      {postmaster.domain_name}
                    </td>
                    <td data-label={t("table.header.account")} className={cx(s.cell, "textOverlflow")}>
                      {postmaster.status === "updating" || postmaster.status === "disabled" ? (
                        postmaster.email_address
                      ) : (
                        <a
                          onClick={() =>
                            navigateToPostmasterSettings(
                              postmaster.code,
                              postmaster.domain_code,
                              postmaster.customer_code
                            )
                          }
                        >
                          {postmaster.email_address}
                        </a>
                      )}
                    </td>
                    <td data-label={t("table.header.quote")} className={s.cell}>
                      <div className={s.quota}>
                        <div className={s.percentage}>{percentage}</div>
                        {!isMediaMobile && <LoadingBar pctg={percentage} />}
                        {!isMediaMobile && <div className={s.giga}>{quotaInGB + "/" + maxQuotaInGB}</div>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostmastersListScreen;
